import {toast} from "sonner";

const ENVELOPE_FLAG = "__cx_clipboard__";

export const SCOPES = {
    BLOCK: ["block"],
    SCHEMA: ["schema"],
    ITEM: ["item"],
    NODE: ["node"],
    PAGE: ["page"],
    ANY: ["any"],
};

function newId() {
    if (typeof crypto !== "undefined" && crypto?.randomUUID) return crypto.randomUUID().replace(/-/g, "");
    const rnd = () => Math.random().toString(36).slice(2);
    return (rnd() + rnd() + rnd()).slice(0, 24);
}

function safeClone(obj) {
    try {
        if (typeof structuredClone === "function") return structuredClone(obj);
    } catch {
    }
    try {
        return JSON.parse(JSON.stringify(obj));
    } catch {
        return {};
    }
}

function applyNewIdentifiers(obj, fields = ["id", "uuid"]) {
    const clone = safeClone(obj);
    fields.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(clone, key)) clone[key] = newId();
    });
    return clone;
}

function sanitisePayload(payload) {
    const cleaned = safeClone(payload);
    [
        "createdAt", "updatedAt", "publishedAt",
        "createdBy", "updatedBy", "publishedBy",
        "_links", "isActive",
    ].forEach((f) => delete cleaned[f]);
    if (cleaned?.imageState?.__cx__?.id) delete cleaned.imageState.__cx__.id;
    return cleaned;
}

async function writeTextResilient(text) {
    // Try modern API first
    try {
        await navigator.clipboard.writeText(text);
        return {ok: true, via: "navigator.clipboard"};
    } catch (err1) {
        // Fallback to execCommand for older browsers/iframes
        try {
            const ta = document.createElement("textarea");
            ta.value = text;
            ta.setAttribute("readonly", "");
            ta.style.position = "fixed";
            ta.style.top = "-9999px";
            document.body.appendChild(ta);
            ta.select();
            const ok = document.execCommand("copy");
            document.body.removeChild(ta);
            if (ok) return {ok: true, via: "execCommand"};
            return {ok: false, via: "execCommand", error: new Error("execCommand returned false")};
        } catch (err2) {
            return {ok: false, via: "execCommand", error: err2, firstError: err1};
        }
    }
}

async function queryClipboardReadPermission() {
    try {
        const status = await navigator.permissions?.query?.({name: "clipboard-read"});
        return status?.state ?? "prompt"; // "granted" | "denied" | "prompt"
    } catch {
        return undefined; // Unknown support
    }
}

/** Harmless read-to-surface paste-permission prompt (won’t throw user-facing errors). */
async function tryPromptPastePermissionOnce() {
    try {
        if (navigator.clipboard?.readText) {
            await navigator.clipboard.readText();
        }
    } catch {
        // ignore — denied or blocked is fine; we’ll handle with a toast after we re-check
    }
}

/** After copy, try to confirm paste access; if not granted, show a clear toast. */
async function ensurePasteAccessOrToast() {
    const state = await queryClipboardReadPermission();

    // If Permissions API is unsupported (Safari) or already granted, do not block copy.
    if (state === undefined || state === "granted") return true;

    // Try to surface the prompt once (only relevant on browsers that support it)
    await tryPromptPastePermissionOnce();

    const after = await queryClipboardReadPermission();

    // Only show an error if the browser explicitly reports 'denied'.
    if (after === "denied") {
        toast.error("Clipboard access is blocked. Enable it in your browser settings, then try again.");
    }

    // Never block copy based on read permission; write permission is separate.
    return true;
}

/**
 * Copy an envelope to the clipboard.
 * After a successful copy:
 *  - We try once to surface the paste permission prompt.
 *  - If the user declines or permission remains blocked, we show a clear toast.
 */
export async function copyToClipboard(kind, payload, scopeKinds = SCOPES.ANY) {
    if (typeof navigator === "undefined") {
        toast.error("Copy isn’t available in this environment.");
        return null;
    }

    const kinds = Array.isArray(scopeKinds) ? scopeKinds : [scopeKinds];

    const envelope = {
        [ENVELOPE_FLAG]: true,
        kind,
        version: "1.0.0",
        meta: {copiedAt: new Date().toISOString()},
        scope: {kinds},
        payload: sanitisePayload(payload),
    };

    let text;
    try {
        text = JSON.stringify(envelope);
    } catch (e) {
        console.error("[Clipboard] stringify failed", e);
        toast.error("Copy failed due to an internal error.");
        return null;
    }
    
    // Try to ensure paste access (best effort, non-blocking on Safari/iOS where Permissions API is limited)
    await ensurePasteAccessOrToast();

    const res = await writeTextResilient(text);
    if (!res.ok) {
        toast.error("Copy failed. Your browser may be blocking clipboard access.");
        return null;
    }

    return envelope;
}

/** Read our structured envelope from the clipboard (may prompt; call from a user gesture). */
export async function readFromClipboard() {
    if (typeof navigator === "undefined") return null;

    let text;
    try {
        text = await navigator.clipboard.readText(); // may prompt
    } catch (err) {
        throw err;
    }

    try {
        const data = JSON.parse(text);
        if (data && data[ENVELOPE_FLAG] === true) return data;
    } catch {
    }
    return null;
}

/**
 * Read from clipboard and ensure content is compatible with the requested kind.
 * Shows a user-facing toast if there is nothing to paste or the kinds don't match.
 * Call this from a direct user gesture (click/tap) so Safari/iOS will allow the read.
 */
export async function readKindOrToast(targetKind) {
    let initialPermissionState;
    try {
        initialPermissionState = await queryClipboardReadPermission();
    } catch {
        initialPermissionState = undefined;
    }

    let env = null;
    let readError = null;
    // Attempt an interactive read (may prompt on some browsers)
    try {
        env = await readFromClipboard();
    } catch (err) {
        readError = err;
    }

    if (!env) {
        let finalPermissionState;
        try {
            finalPermissionState = await queryClipboardReadPermission();
        } catch {
            finalPermissionState = undefined;
        }

        const permissionState = finalPermissionState ?? initialPermissionState;
        const permissionBlocked =
            permissionState === "denied"
            || readError?.name === "NotAllowedError"
            || readError?.name === "SecurityError";

        if (permissionBlocked) {
            toast.error("Clipboard access is blocked in this browser. Allow clipboard permissions for this site and try again.");
        } else {
            toast.error("Nothing to paste");
        }
        return null;
    }

    const kinds = env.scope?.kinds || [];
    const compatible = kinds.includes("any") || kinds.includes(targetKind);
    if (!compatible) {
        toast.error(`Couldn't paste content, please try copy again`);
        return null;
    }

    return env;
}

export function transformOnPaste(envelope, options = {}) {
    const {
        idFields = ["id", "uuid"],
        stripFields = ["createdAt", "updatedAt", "publishedAt"],
    } = options;

    let out = safeClone(envelope.payload);
    out = applyNewIdentifiers(out, idFields);
    stripFields.forEach((field) => delete out[field]);
    return out;
}
